import { useEffect, useState } from "react";

import { RadioButton, Typography } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import { getEnrollmentDecision, getRequest, setOutsideEnrollmentAreaStatus } from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
    ContentArea, ToolbarTop,
    ToolbarEdit, FormCard, RequestHeader
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { Role, ValidationFailure } from "../../models";
import { Institution } from "../../models/Institution";
import {
    Request, RequestStatus, RequestType,
    OutsideEnrollmentAreaStatus, InstitutionChangeReason, EnrollmentDecision
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import { outsideEnrollmentAreaListURL } from "../../routes/routes";
import { setIsEdited } from "../../slices/applicationSlice";
import { MsgUtils } from "../../utils";

export function OutsideEnrollmentAreaEdit() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const auth = useAuth();
    const dispatch = useAppDispatch();

    const createDefault = (): Request => {
        return {
            id: 0,
            requestUID: "",
            code: "",
            statusId: RequestStatus.Draft,
            schoolYearId: 0,
            type: RequestType.RegularEnrollment,
            createdByAdmin: true,
            isOutsideEnrollmentArea: false,
            step: 0,
            subStep: 0,
        };
    };
    const { data: schoolYear } = useGetSchoolYearCurrentQuery();
    const [item, setItem] = useState<Request>(createDefault());
    const [enrollmentDecision, setEnrollmentDecision] = useState<EnrollmentDecision | undefined>();
    const [isApproved, setIsApproved] = useState<boolean>(false);
    const [status, setStatus] = useState<OutsideEnrollmentAreaStatus>(OutsideEnrollmentAreaStatus.NotProcessed);
    const [primaryInstitution, setPrimaryInstitution] = useState<Institution | undefined>();
    const [secondaryInstitution, setSecondaryInstitution] = useState<Institution | undefined>();
    const [errors, setErrors] = useState<ValidationFailure[]>();
    const [isEditing, setIsEditing] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            if (id && id !== "0") {
                var result = (await getRequest(+id));
                if (result) {
                    setItem(result);

                    var enrollmentDecision = (await getEnrollmentDecision(+id));
                    setEnrollmentDecision(enrollmentDecision);

                    var status = result.outsideEnrollmentAreaStatus ?? OutsideEnrollmentAreaStatus.NotProcessed;
                    var canSave = canSaveRequest(result, enrollmentDecision);

                    if (canSave) {
                        switch (status) {
                            case OutsideEnrollmentAreaStatus.NotProcessed:
                                switch (auth.currentRole?.role) {
                                    case Role.CAS:
                                    case Role.Helpdesk:
                                    case Role.InstitutionAdministrator:
                                    case Role.InstitutionPrincipal:
                                        setStatus(OutsideEnrollmentAreaStatus.SuggestedEnrollment);
                                        setIsApproved(result.suggestedEnrollmentOutsideEnrollmentArea ?? false);
                                        dispatch(setIsEdited());
                                        setIsEditing(true);
                                        break;
                                    // Other Roles cannot create and should not be here.
                                }
                                break;
                            case OutsideEnrollmentAreaStatus.SuggestedEnrollment:
                                switch (auth.currentRole?.role) {
                                    case Role.InstitutionAdministrator:
                                    case Role.InstitutionPrincipal:
                                        // These Roles can change their mind.
                                        setStatus(OutsideEnrollmentAreaStatus.SuggestedEnrollment);
                                        setIsApproved(result.suggestedEnrollmentOutsideEnrollmentArea ?? false);
                                        break;
                                    case Role.CAS:
                                    case Role.Helpdesk:
                                    case Role.CountyLeader:
                                    case Role.CountyAdministrator:
                                    case Role.CountyCommittee:
                                    case Role.RegionalCommittee:
                                        // These Roles can change to Decision.
                                        setStatus(OutsideEnrollmentAreaStatus.Decision);
                                        setIsApproved(result.approvedEnrollmentOutsideEnrollmentArea ?? false);
                                        dispatch(setIsEdited());
                                        setIsEditing(true);
                                        break;
                                }
                                break;
                            case OutsideEnrollmentAreaStatus.Decision:
                                switch (auth.currentRole?.role) {
                                    case Role.CAS:
                                    case Role.Helpdesk:
                                    case Role.CountyLeader:
                                    case Role.CountyAdministrator:
                                    case Role.CountyCommittee:
                                    case Role.RegionalCommittee:
                                        // These Roles can change their mind.
                                        setStatus(OutsideEnrollmentAreaStatus.Decision);
                                        setIsApproved(result.approvedEnrollmentOutsideEnrollmentArea ?? false);
                                        break;
                                }
                                break;
                        }
                    }
                }
            }
        })();
        //dispatch(setIsEdited())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    useEffect(() => {
        (async () => {
            if (item.institutionId && item.schoolYearId > 0 && (!primaryInstitution || primaryInstitution.id !== item.institutionId)) {
                setPrimaryInstitution((await getInstitutionById(item.institutionId, item.schoolYearId)));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.institutionId, item.schoolYearId]);
    useEffect(() => {
        (async () => {
            if (item.outsideEnrollmentAreaInstitutionProgram?.institutionId && item.schoolYearId > 0 && (!secondaryInstitution || secondaryInstitution.id !== item.outsideEnrollmentAreaInstitutionProgram?.institutionId)) {
                setSecondaryInstitution((await getInstitutionById(item.outsideEnrollmentAreaInstitutionProgram?.institutionId, item.schoolYearId)));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.outsideEnrollmentAreaInstitutionProgram?.institutionId, item.schoolYearId]);

    const handleIsApprovedChange = (val: boolean) => {
        setIsApproved(val);
        dispatch(setIsEdited())
    };
    const canSave = ():boolean => {
        return canSaveRequest(item, enrollmentDecision);
    };
    const canSaveRequest = (item: Request, enrollmentDecision?: EnrollmentDecision):boolean => {
        var result = (
            auth.currentRole?.role === Role.CAS
            || auth.currentRole?.role === Role.Helpdesk
            || ((auth.currentRole?.role === Role.InstitutionAdministrator
                || auth.currentRole?.role === Role.InstitutionPrincipal)
                && auth.currentRole?.locations !== undefined
                && item.outsideEnrollmentAreaInstitutionProgram !== undefined
                && auth.currentRole.locations.some(v => v === item.outsideEnrollmentAreaInstitutionProgram!.institutionId)
                && (item.outsideEnrollmentAreaStatus ?? OutsideEnrollmentAreaStatus.NotProcessed) <= OutsideEnrollmentAreaStatus.SuggestedEnrollment)
            || ((auth.currentRole?.role === Role.CountyAdministrator
                || auth.currentRole?.role === Role.CountyLeader)
                && auth.currentRole?.locations !== undefined 
                && auth.currentRole?.locations.some(v => v === item.countyId)
                && (item.outsideEnrollmentAreaStatus ?? OutsideEnrollmentAreaStatus.NotProcessed) >= OutsideEnrollmentAreaStatus.SuggestedEnrollment)
            || ((auth.currentRole?.role === Role.CountyCommittee
                || auth.currentRole?.claimType === ClaimType.Committee)
                && item.countyCommitteeId !== undefined
                && (auth.getCommitteeMembershipByCommitteeId(item.countyCommitteeId)?.some(c => c.isPresident) ?? false)))
        && (enrollmentDecision?.solutionId == undefined)
        && (item.schoolYearId === schoolYear?.id)
        && (item.statusId === RequestStatus.Submitted
            || item.statusId === RequestStatus.OpinionWithRecommendation
            || item.statusId === RequestStatus.EnrollmentApproved);

        return result;
    };

    return (
        <ContentArea>
            <ToolbarTop
                showAdd={"hide"}
                showBack={"show"}
                showEdit={isEditing ? "hide" : (canSave() ? "show" : "disable")}
                onClickEdit={() => setIsEditing(true)}
                pageTitle={t("suggestedOutsideEnrollmentArea.pageTitle")}
                errors={errors}
            />
            <RequestHeader request={item} />
            <FormCard>
                <Row>
                    <Col md={12}>
                        <Typography component="span">
                            {t("suggestedOutsideEnrollmentArea.primaryInstitution")}:&nbsp;<b>{primaryInstitution?.name}</b>
                        </Typography>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <Typography>
                            {t("suggestedOutsideEnrollmentArea.secondaryInstitution")}:&nbsp;<b>{secondaryInstitution?.name}</b>
                        </Typography>
                    </Col>
                </Row>
                <Row>
                </Row>
                <Row>
                    <Col md={12}>
                        <Typography>
                            {t("suggestedOutsideEnrollmentArea.status")}:&nbsp;<b>{MsgUtils.getEnumToCodeBook(RequestStatus, "requestStatus").find(v => v.id === item.statusId)?.name}</b>
                        </Typography>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <Typography>
                            {t("suggestedOutsideEnrollmentArea.reason")}:&nbsp;<b>{MsgUtils.getEnumToCodeBook(InstitutionChangeReason, "institutionChangeReason").find(v => v.id === item.institutionChangeReason)?.name}</b>
                        </Typography>
                    </Col>
                </Row>
                {(item.institutionChangeReason === InstitutionChangeReason.Other && item.institutionChangeReasonOther) &&
                <>
                    <Row>
                        <Col md={12}>
                            <Typography>
                                {t("suggestedOutsideEnrollmentArea.reasonOther")}:&nbsp;<b>{item.institutionChangeReasonOther}</b>
                            </Typography>
                        </Col>
                    </Row>
                </>
                }

                {(status === OutsideEnrollmentAreaStatus.SuggestedEnrollment || item.suggestedEnrollmentOutsideEnrollmentArea || enrollmentDecision) &&
                    <>
                        <Row>
                        </Row>
                        {(isEditing && status === OutsideEnrollmentAreaStatus.SuggestedEnrollment) ?
                        (<>
                        <Row>
                            <Col md={12}>
                                <Typography variant="baseBold">
                                    {t("suggestedOutsideEnrollmentArea.isApproved")}
                                </Typography>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                            </Col>
                            <Col md={6}>
                                <RadioButton
                                    ariaLabel={t("common.yes")}
                                    label={t("common.yes")}
                                    name={"yes"}
                                    checked={isApproved}
                                    onChange={(e) => handleIsApprovedChange(true)}
                                    disabled={!isEditing || item.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.Decision}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                            </Col>
                            <Col md={6}>
                                <RadioButton
                                    ariaLabel={t("common.no")}
                                    label={t("common.no")}
                                    checked={!isApproved}
                                    name={"no"}
                                    onChange={(e) => handleIsApprovedChange(false)}
                                    disabled={!isEditing || item.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.Decision}
                                />
                            </Col>
                        </Row>
                        </>)
                        :
                        (<Row>
                            <Col md={12}>
                                <Typography>
                                    {t("suggestedOutsideEnrollmentArea.secondaryInstitutionApproved")}:&nbsp;<b>{enrollmentDecision?.solutionId != undefined ? t("suggestedOutsideEnrollmentArea.approvedByEnrollmentDecision") : ((item.suggestedEnrollmentOutsideEnrollmentArea ?? false) ? t("common.yes") : t("common.no"))}</b>
                                </Typography>
                            </Col>
                        </Row>)
                        }
                    </>
                }
                {(status === OutsideEnrollmentAreaStatus.Decision || item.approvedEnrollmentOutsideEnrollmentArea || enrollmentDecision) &&
                    <>
                        <Row>
                        </Row>
                        {(isEditing && status === OutsideEnrollmentAreaStatus.Decision) ?
                        (<>
                        <Row>
                            <Col md={12}>
                                <Typography variant="baseBold">
                                    {t("suggestedOutsideEnrollmentArea.countyIsApproved")}:
                                </Typography>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={2}>
                            </Col>
                            <Col md={10}>
                                <RadioButton
                                    ariaLabel={t("common.no")}
                                    label={t("suggestedOutsideEnrollmentArea.primaryInstitution") + ": " + primaryInstitution?.name}
                                    checked={!isApproved}
                                    name={"no"}
                                    onChange={(e) => handleIsApprovedChange(false)}
                                    disabled={!isEditing}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={2}>
                            </Col>
                            <Col md={10}>
                                <RadioButton
                                    ariaLabel={t("common.yes")}
                                    label={t("suggestedOutsideEnrollmentArea.secondaryInstitution") + ": " + secondaryInstitution?.name}
                                    checked={isApproved}
                                    name={"yes"}
                                    onChange={(e) => handleIsApprovedChange(true)}
                                    disabled={!isEditing}
                                />
                            </Col>
                        </Row>
                        </>)
                        :
                        (<>
                        <Row>
                            <Col md={12}>
                                <Typography variant="baseBold">
                                    {t("suggestedOutsideEnrollmentArea.countyIsApproved")}:&nbsp;<b>{enrollmentDecision?.solutionId != undefined ? t("suggestedOutsideEnrollmentArea.approvedByEnrollmentDecision") : (item.outsideEnrollmentAreaStatus === OutsideEnrollmentAreaStatus.Decision && (item.approvedEnrollmentOutsideEnrollmentArea ?? false) ? t("common.yes") : t("common.no"))}</b>
                                </Typography>
                            </Col>
                        </Row>
                        </>)
                        }
                    </>
                }
            </FormCard>
            <ToolbarEdit
                showCancel={"show"}
                showSave={canSave() ? "show" : "disable"}
                redirectUrlSaveOK={outsideEnrollmentAreaListURL}
                handleSave={async () => setOutsideEnrollmentAreaStatus(item.id, status, isApproved)}
                setSaveErrors={setErrors}
            />
        </ContentArea>
    );
}